import './styles.modules.scss';
import { MdAddShoppingCart } from '@react-icons/all-files/md/MdAddShoppingCart';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'reactstrap';
import InputQuantity from '../../Quantity';
import { Product, ProductType } from '~redux/brand/types';

type OrderTableColProps = {
  color: string;
  sizes: string[];
  selectedColor: string;
  onSelectColor: (color: string) => void;
  cart?: boolean;
  productId: string;
  preview?: boolean;
  product: Product;
  productTypes: ProductType[];
};

const OrderTableCol: React.FC<OrderTableColProps> = ({
  color,
  sizes,
  selectedColor,
  onSelectColor,
  cart,
  productId,
  preview,
  product,
  productTypes,
}) => {
  const { values, isSubmitting, setFieldValue, setValues } = useFormikContext<{
    color: string;
    quantities: Record<string, number>;
  }>();

  const totalQuantity = useMemo<number | undefined>(
    () =>
      sizes.reduce((prev, size) => {
        const type = productTypes.find(
          (type) => type.size === size && type.color === color
        );
        const qty = Number(values.quantities[type?.id ?? '']);
        return isNaN(qty) ? prev : (prev ?? 0) + qty;
      }, undefined as number | undefined),
    [productTypes, values, sizes, color]
  );

  const [isCompleted, setIsCompleted] = useState<boolean>();
  useEffect(() => {
    if (isSubmitting) {
      setIsCompleted(false);
      return;
    }
    if (isCompleted === false) {
      setIsCompleted(true);
      setTimeout(() => setIsCompleted(false), 1000);
    }
  }, [isSubmitting]);

  const lot = useMemo(() => product.product_order_lot ?? 1, [product]);
  const max = useMemo(() => 1000 - (1000 % lot), [product, lot]);
  const min = useMemo(
    () =>
      Math.min(
        product.product_minimum_quantity ?? 1,
        product.product_order_lot ?? 1
      ),
    [product]
  );

  const incrementAll = useCallback(() => {
    const quantities = Object.entries(values.quantities).reduce(
      (prev, [typeId, quantity]) => {
        const productType = productTypes.find((t) => t.id === typeId);
        if (productType?.color !== color || productType.out_of_stock) {
          return { ...prev, [typeId]: quantity };
        }
        return { ...prev, [typeId]: Math.min((quantity ?? 0) + lot, max) };
      },
      {}
    );
    setValues({ ...values, quantities });
  }, [color, productTypes, values, lot, max]);

  const decrementAll = useCallback(() => {
    const quantities = Object.entries(values.quantities).reduce(
      (prev, [typeId, quantity]) => {
        const productType = productTypes.find((t) => t.id === typeId);
        if (productType?.color !== color) {
          return { ...prev, [typeId]: quantity };
        }
        return { ...prev, [typeId]: Math.max((quantity ?? 0) - lot, 0) };
      },
      {}
    );
    setValues({ ...values, quantities });
  }, [color, productTypes, values, lot]);

  return (
    <div
      id={`product-type-col-${productId}-${color}`}
      styleName={`col${color === selectedColor ? ' selected' : ''}`}
      onClick={() => onSelectColor(color)}
    >
      <div styleName="cell">
        <div styleName="color">{color}</div>
      </div>
      {sizes.map((size) => {
        const productType = productTypes.find(
          (type) => type.color === color && type.size === size
        );
        // const inventory = inventories?.[productType?.sku!];
        // const limit =
        //   (cart ? inventory?.inventory : inventory?.availableInventory) ?? max;
        if (!productType) {
          return <div styleName="cell qty not-on-sale" key={size} />;
        }

        if (
          productType.product?.product_preorder !== 1 && // 予約販売の場合は売り切れにしない
          productType.out_of_stock
        ) {
          return (
            <div styleName="cell qty" className="text-muted">
              売り切れ
            </div>
          );
        }

        return (
          <div styleName="cell qty" key={size}>
            <InputQuantity
              name={productType.id}
              minimum={min}
              lot={lot}
              brandId={product.product_brand_id!}
              productId={productId}
              productTypeId={productType?.id}
              sku={productType?.sku}
              cart={cart}
            />
          </div>
        );
      })}

      <div styleName="cell cart">
        {totalQuantity === undefined && (totalQuantity ?? 0)}
        {cart && totalQuantity !== undefined && (
          <>
            <button
              type="button"
              tabIndex={-1}
              onClick={decrementAll}
              className="bg-white mr-2"
              style={{
                border: 0,
                width: '30px',
                padding: '5px 0',
              }}
            >
              -
            </button>
            <div className="mr-2 text-center" style={{ width: '60px' }}>
              {totalQuantity ?? 0}
            </div>
            <button
              type="button"
              tabIndex={-1}
              onClick={incrementAll}
              className="bg-white"
              style={{
                border: 0,
                width: '30px',
                padding: '5px 0',
              }}
            >
              +
            </button>
          </>
        )}
        {!cart && totalQuantity !== undefined && (
          <>
            <button
              type="button"
              tabIndex={-1}
              onClick={decrementAll}
              className="bg-white"
              style={{
                border: 0,
                width: '30px',
                padding: '5px 0',
              }}
            >
              -
            </button>
            <button
              disabled={
                !totalQuantity ||
                (values.color === color && isSubmitting) ||
                preview
              }
              type="submit"
              styleName="add-to-cart"
              onClick={() => setFieldValue('color', color, false)}
            >
              {values.color === color && isSubmitting ? (
                <Spinner size="sm" />
              ) : values.color === color && isCompleted ? (
                <i styleName="cart-done" />
              ) : (
                <>
                  <MdAddShoppingCart className="mr-0" />
                  {totalQuantity > 999 ? 999 : totalQuantity}
                </>
              )}
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={incrementAll}
              className="bg-white"
              style={{
                border: 0,
                width: '30px',
                padding: '5px 0',
              }}
            >
              +
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderTableCol;
